import { ProgressIndicator, Stack } from "@fluentui/react";
import { Pivot, PivotItem } from "@fluentui/react/lib/Pivot";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { BackButton } from "../../components/BackButton/BackButton";
import { ContractStatus, TranslatedContractOrigin } from "../../enums";

import { getContractSteps } from "./contractSteps";
import { calcProgressBar } from "./utils/calcProgressBar";
import { findMonthlyPaymentMethod } from "./utils/findMonthlyPaymentMethod";
import { verifyContractSteps } from "./verifyContractSteps";

import { Contract } from "@/components/Contracts/contract.interface";
import { Loader } from "@/components/Shared/Loader";
import {
  IMessageBar,
  MessageBar,
  messageBarTypes
} from "@/core/libs/message-bar";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { getContract } from "@/modules/Contracts/api";
import { VerifyContractStepFunc } from "@/modules/Contracts/types";
import { formatDate } from "@/utils/FormatDate";

export function ContractsEdit(): JSX.Element {
  const { handleSelectBu, selectedBU, selectedRegion, selectedSegment } =
    useSelectedBu();
  const [message, setMessage] = useState<IMessageBar>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [maxStep, setMaxStep] = useState<number>(0);
  const topOfPageScrollRef = useRef<HTMLDivElement>(null);

  const { id } = useParams<"id">();
  const [contractId] = useState<string | undefined>(id);

  const [contract, setContract] = useState<Contract>(undefined);

  const [monthlyPaymentMethod, setMonthlyPaymentMethod] = useState<string>();
  const [generateContractTerms, setGenerateContractTerms] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cleanMessage = () => setMessage(undefined);
  const contractSteps = getContractSteps(contract);

  const processVerifyContractSteps = useCallback(
    ({
      currentContract,
      setCurrentStep,
      currentStep,
      contractSteps
    }: VerifyContractStepFunc) => {
      verifyContractSteps({
        selectedBU,
        setMaxStep,
        currentContract,
        setCurrentStep,
        currentStepOrder: currentStep,
        contractSteps
      });
    },
    [selectedBU]
  );

  const fetchData = useCallback(async () => {
    const response = await getContract(contractId);

    if (response.error) {
      setIsLoading(false);
      setMessage({
        message:
          "Não foi possível localizar o contrato, por favor atualize a página e tente novamente",
        type: messageBarTypes.ERROR
      });
    } else {
      const { data } = response;

      if (data.currentStep.internalName !== ContractStatus.IN_PROGRESS) {
        navigate(`/contracts/${data.id}`);
      } else {
        setContract(data);
        processVerifyContractSteps({
          currentContract: data,
          setCurrentStep,
          currentStep,
          contractSteps
        });
        setMonthlyPaymentMethod(findMonthlyPaymentMethod(data));
        setIsLoading(false);
      }
    }
  }, [
    contractId,
    contractSteps,
    currentStep,
    navigate,
    processVerifyContractSteps
  ]);

  useEffect(() => {
    if (contract?.businessUnit && selectedBU?.id !== contract.businessUnit.id) {
      const businessUnitDisplayName = selectedBU?.name;
      const breadcrumbParts: string[] = [];

      if (selectedRegion) breadcrumbParts.push(selectedRegion);

      if (selectedSegment) breadcrumbParts.push(selectedSegment);

      if (businessUnitDisplayName) {
        breadcrumbParts.push(businessUnitDisplayName);
      }

      setMessage({
        message: `O contrato ${contractId} não pertence a ${breadcrumbParts.join(" > ")}.
         Devido a isso, redirecionamos seu perfil para: ${contract.businessUnit.name}, a qual o respectivo contrato pertence.`,
        type: messageBarTypes.WARNING
      });

      handleSelectBu(contract.businessUnit.id);
    }
  }, [
    selectedBU,
    contract,
    dispatch,
    contractId,
    handleSelectBu,
    selectedRegion,
    selectedSegment
  ]);

  useEffect(() => {
    setIsLoading(true);
    void fetchData();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      topOfPageScrollRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, [currentStep, isLoading]);

  return (
    <div ref={topOfPageScrollRef}>
      <Helmet>
        <title>Confecção de contrato - Libertas</title>
      </Helmet>

      {message && (
        <MessageBar message={message} dismissMessage={cleanMessage} />
      )}

      {isLoading ? (
        <ProgressIndicator
          styles={{
            root: { position: "absolute", left: 0, right: 0, top: 0 },
            itemProgress: { padding: 0 }
          }}
        />
      ) : (
        <Stack tokens={{ childrenGap: 15 }}>
          <BackButton />

          <h2 className="ms-fontSize-24" style={{ marginTop: 0 }}>
            Novo contrato - {TranslatedContractOrigin[contract?.origin]}
          </h2>

          {contract?.deletedAt && (
            <MessageBar
              message={{
                message: `Este contrato foi excluído em ${formatDate(
                  contract.deletedAt
                )} e nenhuma ação pode ser feita nele.`,
                type: messageBarTypes.WARNING
              }}
            />
          )}

          <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12">
                <ProgressIndicator
                  percentComplete={calcProgressBar({
                    currentStep,
                    numberOfContractSteps: contractSteps?.length
                  })}
                  styles={{ root: { width: "75%", margin: "auto" } }}
                />
              </div>
            </div>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12">
                <Pivot
                  styles={{
                    root: {
                      justifyContent: "space-around",
                      display: "flex"
                    },
                    link: { marginRight: 0 },
                    linkIsSelected: { marginRight: 0 }
                  }}
                  selectedKey={String(currentStep)}
                  onLinkClick={(item?: PivotItem) => {
                    const nextStep = Number(item?.props.itemKey);

                    if (nextStep <= maxStep) {
                      setCurrentStep(nextStep);
                    }
                  }}
                >
                  {contractSteps?.map(
                    ({ label, component: StepComponent }, index) => (
                      <PivotItem
                        key={index}
                        itemKey={String(index)}
                        headerText={label}
                        headerButtonProps={{
                          "data-testid": `step-button-${index}`,
                          disabled: index > maxStep
                        }}
                      >
                        <Stack styles={{ root: { padding: 20 } }}>
                          {isLoading && <Loader />}
                          {!isLoading && currentStep === index && (
                            <StepComponent
                              contract={contract}
                              nextStep={fetchData}
                              setContract={setContract}
                              verifyContractSteps={processVerifyContractSteps}
                              setContractMessage={setMessage}
                              refetchContractData={fetchData}
                              monthlyPaymentMethod={monthlyPaymentMethod}
                              generateContractTerms={generateContractTerms}
                              setGenerateContractTerms={
                                setGenerateContractTerms
                              }
                            />
                          )}
                        </Stack>
                      </PivotItem>
                    )
                  )}
                </Pivot>
              </div>
            </div>
          </div>
        </Stack>
      )}
    </div>
  );
}
